import { v4 as uuid } from "uuid";
import content from "../content/sdleDemo.json";

export interface DemoQuestion {
  id: string;
  specialty: string;
  question_text: string;
  options: { id: string; label: string; text: string }[];
  correct_option_id: string;
  correct_answer: string;
  explanation: string;
  references: { title: string; url: string }[];
}

export interface DemoAttempt {
  id: string;
  questions: DemoQuestion[];
  total_questions: number;
  duration_seconds: number;
}

export interface DemoResults {
  questions: (DemoQuestion & { user_answer: string | null; is_correct: boolean })[];
  specialty_breakdown: { specialty: string; total: number; correct: number }[];
  score: { correct: number; total: number; percentage: number };
  time_taken_seconds: number;
}

export const PUBLIC_DEMO_VERSION = content.version;
export const DEMO_DURATION_SECONDS = 600;

// The option ID is the answer key. Display letters are derived together from
// the same bundled content, never maintained as a separate answer mapping.
export function demoQuestions(): DemoQuestion[] {
  return content.questions.map(question => {
    const options = question.options.map((option, index) => ({
      ...option, label: String.fromCharCode(65 + index),
    }));
    return {
      ...question,
      options,
      references: question.references.map(reference => ({ ...reference })),
      correct_answer: options.find(option => option.id === question.correct_option_id)!.label,
    };
  });
}

export const publicSample = demoQuestions().find(question => question.id === content.sample_id)!;

export function createDemoAttempt(): DemoAttempt {
  const questions = demoQuestions();
  return { id: uuid(), questions, total_questions: questions.length, duration_seconds: DEMO_DURATION_SECONDS };
}

export function gradeDemo(
  questions: DemoQuestion[],
  answers: Record<string, string>,
  elapsed: number,
): DemoResults {
  const specialties = new Map<string, { specialty: string; total: number; correct: number }>();
  const graded = questions.map(question => {
    const selected = question.options.find(option => option.id === answers[question.id]);
    const isCorrect = selected?.id === question.correct_option_id;
    const stat = specialties.get(question.specialty) || { specialty: question.specialty, total: 0, correct: 0 };
    stat.total += 1;
    stat.correct += Number(isCorrect);
    specialties.set(question.specialty, stat);
    return { ...question, user_answer: selected?.label || null, is_correct: isCorrect };
  });
  const correct = graded.filter(question => question.is_correct).length;
  return {
    questions: graded,
    specialty_breakdown: Array.from(specialties.values()),
    score: { correct, total: questions.length, percentage: questions.length ? Math.round(correct / questions.length * 1000) / 10 : 0 },
    time_taken_seconds: Number.isFinite(elapsed) ? Math.max(0, Math.min(DEMO_DURATION_SECONDS, Math.floor(elapsed))) : 0,
  };
}

const SAVED_RESULT_KEY = "cshaped_public_demo_result_v2";
const RESULT_TTL_MS = 24 * 60 * 60 * 1000;

export function clearDemoResult(): void {
  try { localStorage.removeItem(SAVED_RESULT_KEY); } catch { /* Optional device storage. */ }
}

export function saveDemoResult(answers: Record<string, string>, elapsed: number): void {
  try {
    localStorage.setItem(SAVED_RESULT_KEY, JSON.stringify({
      version: PUBLIC_DEMO_VERSION, expires_at: Date.now() + RESULT_TTL_MS,
      answers, elapsed,
    }));
  } catch { /* Results still work when device storage is unavailable. */ }
}

export function readDemoResult(): DemoResults | null {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED_RESULT_KEY) || "null");
    if (!saved || saved.version !== PUBLIC_DEMO_VERSION || !Number.isFinite(saved.expires_at)
      || saved.expires_at <= Date.now() || !saved.answers || typeof saved.answers !== "object"
      || Array.isArray(saved.answers) || !Number.isFinite(saved.elapsed)) {
      clearDemoResult();
      return null;
    }
    return gradeDemo(demoQuestions(), saved.answers, saved.elapsed);
  } catch { return null; }
}
